import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box, Paper, Typography, Chip, LinearProgress,
  Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer, Button, Stack, Alert
} from "@mui/material";
import api from "../axiosConfig";

const chipForStatus = (status) => {
  const s = String(status || "").toLowerCase();
  if (s === "sent") return <Chip label="Sent" color="success" size="small" />;
  if (s === "failed") return <Chip label="Failed" color="error" size="small" />;
  if (s === "queued") return <Chip label="Pending" color="warning" size="small" />;
  if (s === "sending") return <Chip label="Sending" color="info" size="small" />;
  return <Chip label={status || "Unknown"} size="small" />;
};

export default function MassEmailStatus({ campaignId, onBack }) {
  const [loading, setLoading] = useState(true);
  const [campaign, setCampaign] = useState(null);
  const [rows, setRows] = useState([]);
  const [pageMeta, setPageMeta] = useState({
    next_page_url: null,
    prev_page_url: null,
    current_page: 1,
  });
  const [error, setError] = useState("");

  // keep latest values for polling without re-creating interval
  const currentPageRef = useRef(1);
  const campaignRef = useRef(null);
  const inflightRef = useRef(false);

  useEffect(() => {
    currentPageRef.current = pageMeta.current_page || 1;
  }, [pageMeta.current_page]);

  useEffect(() => {
    campaignRef.current = campaign;
  }, [campaign]);

  const fetchPage = useCallback(
    async (urlOrPage = 1) => {
      if (!campaignId) return;
      if (inflightRef.current) return; // prevent overlapping fetches
      inflightRef.current = true;

      setLoading(true);
      setError("");

      try {
        const url =
          typeof urlOrPage === "string"
            ? urlOrPage
            : `/email/campaigns/${campaignId}/recipients?per_page=100&page=${urlOrPage}`;

        const res = await api.get(url);

        const nextCampaign = res.data?.campaign || null;
        setCampaign(nextCampaign);

        const paginated = res.data?.recipients || {};
        const dataRows = Array.isArray(paginated?.data) ? paginated.data : [];

        setRows(dataRows);
        setPageMeta({
          next_page_url: paginated?.next_page_url || null,
          prev_page_url: paginated?.prev_page_url || null,
          current_page: paginated?.current_page || (typeof urlOrPage === "number" ? urlOrPage : 1),
        });
      } catch (e) {
        console.error("MassEmailStatus fetch failed:", e);
        setError(e?.response?.data?.message || "Failed to load campaign status.");
      } finally {
        inflightRef.current = false;
        setLoading(false);
      }
    },
    [campaignId]
  );

  // initial load
  useEffect(() => {
    if (!campaignId) return;
    fetchPage(1);
  }, [campaignId, fetchPage]);

  // stable polling (doesn't recreate interval on each render)
  useEffect(() => {
    if (!campaignId) return;

    const t = setInterval(() => {
      const c = campaignRef.current;
      const queued = Number(c?.totals?.queued || 0);
      const status = String(c?.status || "").toLowerCase();

      // Only poll while still processing
      if (status === "sending" || queued > 0 || !c) {
        fetchPage(currentPageRef.current);
      }
    }, 2500);

    return () => clearInterval(t);
  }, [campaignId, fetchPage]);

  const header = useMemo(() => {
    const t = campaign?.totals || {};
    return {
      subject: campaign?.subject || "",
      status: campaign?.status || "",
      total: Number(t.total || 0),
      sent: Number(t.sent || 0),
      queued: Number(t.queued || 0),
      failed: Number(t.failed || 0),
    };
  }, [campaign]);

  return (
    <Box sx={{ p: 2 }}>
      <Paper sx={{ p: 2, borderRadius: 3 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="h6" fontWeight={700}>Campaign Status</Typography>
            {header.subject ? (
              <Typography variant="body2" color="text.secondary">{header.subject}</Typography>
            ) : null}
            {campaignId ? (
              <Typography variant="caption" color="text.secondary">Campaign ID: {campaignId}</Typography>
            ) : null}
          </Box>

          <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
            <Chip label={`Total: ${header.total}`} size="small" />
            <Chip label={`Sent: ${header.sent}`} color="success" size="small" />
            <Chip label={`Pending: ${header.queued}`} color="warning" size="small" />
            <Chip label={`Failed: ${header.failed}`} color="error" size="small" />
            <Chip label={`Campaign: ${header.status || "unknown"}`} size="small" />
          </Stack>
        </Stack>

        {error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : null}

        {(loading || String(header.status).toLowerCase() === "sending" || header.queued > 0) && (
          <LinearProgress sx={{ mt: 2, borderRadius: 1 }} />
        )}

        <TableContainer sx={{ mt: 2, border: "1px solid rgba(0,0,0,0.12)", borderRadius: 2, maxHeight: 620 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Sent At</TableCell>
                <TableCell>Last Error</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r, idx) => {
                const key = r?.id ?? `${r?.email || "row"}-${idx}`;
                return (
                  <TableRow key={key} hover>
                    <TableCell>{chipForStatus(r.status)}</TableCell>
                    <TableCell>{r.name || "-"}</TableCell>
                    <TableCell>{r.email || "-"}</TableCell>
                    <TableCell>{r.sent_at ? new Date(r.sent_at).toLocaleString() : "-"}</TableCell>
                    <TableCell>{r.last_error || "-"}</TableCell>
                  </TableRow>
                );
              })}

              {!rows.length && !loading && !error && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography align="center" color="text.secondary">No recipients found.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Stack direction="row" spacing={1} sx={{ mt: 2 }} justifyContent="space-between" flexWrap="wrap">
          <Button variant="outlined" onClick={onBack}>Back</Button>

          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              disabled={!pageMeta.prev_page_url || loading}
              onClick={() => pageMeta.prev_page_url && fetchPage(pageMeta.prev_page_url)}
            >
              Prev
            </Button>
            <Button
              variant="outlined"
              disabled={!pageMeta.next_page_url || loading}
              onClick={() => pageMeta.next_page_url && fetchPage(pageMeta.next_page_url)}
            >
              Next
            </Button>
            <Button
              variant="contained"
              disabled={loading}
              onClick={() => fetchPage(pageMeta.current_page)}
            >
              Refresh
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}